package com.mencanhealth.admin.ui

import androidx.compose.runtime.*
import com.mencanhealth.admin.data.AdminFirestoreService
import com.mencanhealth.admin.model.DeletionRequest
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date

@Composable
fun DeletionRequestsPage(firestoreService: AdminFirestoreService) {
    var requests by remember { mutableStateOf<List<DeletionRequest>>(emptyList()) }
    var filter by remember { mutableStateOf("all") }
    var loading by remember { mutableStateOf(true) }
    var confirmRequest by remember { mutableStateOf<DeletionRequest?>(null) }
    var processing by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        requests = firestoreService.listDeletionRequests()
        loading = false
    }

    val filtered = requests.filter { r ->
        when (filter) {
            "pending" -> r.status == "pending"
            "completed" -> r.status == "completed"
            "rejected" -> r.status == "rejected"
            else -> true
        }
    }

    // Confirmation dialog
    confirmRequest?.let { req ->
        Div({ classes("dialog-overlay"); onClick { confirmRequest = null } }) {
            Div({ classes("dialog-card"); onClick { it.stopPropagation() } }) {
                H3 { Text("Confirmar eliminacion") }
                P { Text("Se eliminaran todos los datos de Firestore para ${req.email}. Esta accion no se puede deshacer.") }
                P({
                    style { property("font-size", "13px"); property("color", "#c62828") }
                }) { Text("Nota: La cuenta de Firebase Auth debe eliminarse manualmente desde Firebase Console.") }
                Div({ classes("dialog-actions") }) {
                    Button({
                        classes("btn-secondary")
                        onClick { confirmRequest = null }
                    }) { Text("Cancelar") }
                    Button({
                        classes("btn-danger")
                        if (processing) attr("disabled", "")
                        onClick {
                            processing = true
                            scope.launch {
                                try {
                                    firestoreService.executeDeletion(req)
                                    requests = firestoreService.listDeletionRequests()
                                    message = "Datos eliminados para ${req.email}"
                                } catch (e: Exception) {
                                    message = "Error: ${e.message}"
                                }
                                processing = false
                                confirmRequest = null
                            }
                        }
                    }) { Text(if (processing) "Eliminando..." else "Ejecutar") }
                }
            }
        }
    }

    Div({ classes("page") }) {
        H2 { Text("Solicitudes de Eliminacion") }

        Div({ classes("info-banner") }) {
            Text("Las cuentas de Firebase Auth deben eliminarse manualmente desde Firebase Console despues de ejecutar la eliminacion de datos.")
        }

        message?.let { msg ->
            Div({
                classes(if (msg.startsWith("Error")) "error-banner" else "success-text")
                style { property("margin-bottom", "16px") }
            }) { Text(msg) }
        }

        Div({ classes("toolbar") }) {
            Div({ classes("filter-chips") }) {
                listOf("all" to "Todos", "pending" to "Pendientes", "completed" to "Completados", "rejected" to "Rechazados").forEach { (key, label) ->
                    Button({
                        classes("chip"); if (filter == key) classes("chip-active")
                        onClick { filter = key }
                    }) { Text(label) }
                }
            }
        }

        if (loading) {
            P { Text("Cargando solicitudes...") }
        } else if (filtered.isEmpty()) {
            Div({ classes("empty-state") }) { Text("No hay solicitudes") }
        } else {
            Div({ classes("user-count") }) { Text("${filtered.size} solicitudes") }
            Table({ classes("data-table") }) {
                Thead {
                    Tr {
                        Th { Text("Email") }
                        Th { Text("Razon") }
                        Th { Text("Fecha") }
                        Th { Text("Estado") }
                        Th { Text("Acciones") }
                    }
                }
                Tbody {
                    filtered.forEach { req ->
                        Tr {
                            Td { Text(req.email) }
                            Td { Text(req.reason.take(80)) }
                            Td { Text(formatDate(req.requestedAt)) }
                            Td { StatusBadge(req.status) }
                            Td {
                                if (req.status == "pending") {
                                    Button({
                                        classes("btn-danger")
                                        style { property("margin-right", "8px") }
                                        onClick { confirmRequest = req }
                                    }) { Text("Ejecutar") }
                                    Button({
                                        classes("btn-secondary")
                                        onClick {
                                            scope.launch {
                                                firestoreService.rejectDeletion(req.id)
                                                requests = firestoreService.listDeletionRequests()
                                                message = "Solicitud rechazada"
                                            }
                                        }
                                    }) { Text("Rechazar") }
                                } else {
                                    Text("-")
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun StatusBadge(status: String) {
    Span({
        classes("status-badge", "status-$status")
    }) {
        Text(when (status) {
            "pending" -> "Pendiente"
            "completed" -> "Completado"
            "rejected" -> "Rechazado"
            else -> status
        })
    }
}

private fun formatDate(timestamp: Long): String {
    if (timestamp == 0L) return "-"
    val d = Date(timestamp.toDouble())
    val day = d.getDate().toString().padStart(2, '0')
    val month = (d.getMonth() + 1).toString().padStart(2, '0')
    val year = d.getFullYear()
    return "$day/$month/$year"
}
